import React from 'react';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import DashboardIcon from '@material-ui/icons/Dashboard';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import StorefrontIcon from '@material-ui/icons/Storefront';
import CloudIcon from '@material-ui/icons/Cloud';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import HomeIcon from '@material-ui/icons/Home';
import SearchIcon from '@material-ui/icons/Search';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import ListIcon from '@material-ui/icons/List';
import TimelineIcon from '@material-ui/icons/Timeline';
import SubscriptionsIcon from '@material-ui/icons/Subscriptions';
import SettingsInputComponentIcon from '@material-ui/icons/SettingsInputComponent';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import BuildIcon from '@material-ui/icons/Build';
import StorageIcon from '@material-ui/icons/Storage';
import CodeIcon from '@material-ui/icons/Code';
import {
  SidebarDivider,
  SidebarItem,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

interface FeatureEntry {
  enabled: boolean;
  place: number;
}

interface Props {
  features: {
    search:        FeatureEntry;
    home:          FeatureEntry;
    marketplace:   FeatureEntry;
    harness:       FeatureEntry;
    quay:          FeatureEntry;
    subscriptions: FeatureEntry;
    catalog:       FeatureEntry;
    announcements: FeatureEntry;
    links:         FeatureEntry;
    documentation: FeatureEntry;
    ideas:         FeatureEntry;
    topology:      FeatureEntry;
    services:      FeatureEntry;
    techRadar:     FeatureEntry;
  };
  showNotifications?: boolean;
}

type FeatureKey = keyof Props['features'];

const ITEM_ELEMENTS: Record<FeatureKey, JSX.Element> = {
  search:        <SidebarItem icon={SearchIcon} to="/search" text="Search" />,
  home:          <SidebarItem icon={HomeIcon} to="/home" text="Home" />,
  marketplace:   <SidebarItem icon={StorefrontIcon} to="/ai-marketplace" text="AI Marketplace" />,
  harness:       null as any, // rendered via SubscriptionsPage
  quay:          null as any, // rendered via SubscriptionsPage
  subscriptions: <SidebarItem icon={SubscriptionsIcon} to="/subscriptions" text="Subscriptions" />,
  catalog:       <SidebarItem icon={ListIcon} to="/catalog" text="Software Catalog" />,
  announcements: <SidebarItem icon={AnnouncementIcon} to="/announcements" text="Announcements" />,
  links: (
    <SidebarItem icon={CloudIcon} to="/links" text="Links">
      <SidebarSubmenu title="Links">
        <SidebarSubmenuItem title="OKD Consoles" to="/okd-consoles" icon={CloudIcon} />
        <SidebarSubmenuItem title="Hydra Tools" to="/our-tools-menu" icon={CloudIcon} />
        <SidebarSubmenuItem title="Datadog" to="/datadog" icon={TimelineIcon} />
      </SidebarSubmenu>
    </SidebarItem>
  ),
  documentation: <SidebarItem icon={MenuBookIcon} to="/documentation-menu" text="Documentation" />,
  ideas:         <SidebarItem icon={DashboardIcon} to="/ideas" text="Submit Idea" />,
  topology:      <SidebarItem icon={AccountTreeIcon} to="/topology-viewer" text="Topology" />,
  services: (
    <SidebarItem icon={GroupAddIcon} to="/topology-viewer/builder" text="Services">
      <SidebarSubmenu title="Services">
        <SidebarSubmenuItem title="Service Registry Builder" to="/topology-viewer/builder" icon={BuildIcon} />
        <SidebarSubmenuItem title="GES Builder" to="/ges-builder" icon={StorageIcon} />
        <SidebarSubmenuItem title="GraviteeAPI Operator Builder" to="/gravitee-api-operator-builder" icon={CodeIcon} />
        <SidebarSubmenuItem title="APIM Application Lookup" to="/apim/applications" icon={SettingsInputComponentIcon} />
      </SidebarSubmenu>
    </SidebarItem>
  ),
  techRadar:     <SidebarItem icon={TrackChangesIcon} to="/tech-radar" text="Tech Landscape" />,
};

export const SidebarPartial = ({ features, showNotifications }: Props) => {
  const sorted = (Object.keys(features) as FeatureKey[])
    .filter(key => features[key].enabled && ITEM_ELEMENTS[key] !== null)
    .sort((a, b) => features[a].place - features[b].place);

  const searchIdx = sorted.indexOf('search');

  return (
    <>
      {sorted.map((key, idx) => (
        <React.Fragment key={key}>
          {ITEM_ELEMENTS[key]}
          {idx === searchIdx && <SidebarDivider />}
        </React.Fragment>
      ))}
      {showNotifications && <NotificationsSidebarItem webNotificationsEnabled />}
    </>
  );
};
